import type { ConnectorConfig, ConnectorFormSuggestion } from '../../../types/data-sources/define';
import type { ConnectionStringIssue } from '../connection-string';
import * as z from 'zod/v4-mini';
import { nonEmptyString } from '#layers/base';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { SUPPORTED_PRIMITIVE_TYPES } from '#layers/data-model/constants/schema/primitive-type';
import { ConnectorType, defineConnector } from '../../../types/data-sources/define';
import { createConnectionStringOptionsChangeHandler, parseConnectionString } from '../connection-string';
import { caCertField, SslMode, sslModeOptions, sslSchema } from './ssl';

/**
 * MySQL/MariaDB SSL modes. No `prefer` (unsupported by the engine) and no
 * mutual TLS (Postgres-only).
 */
const MYSQL_MODES = [SslMode.disable, SslMode.require, SslMode.verifyCa, SslMode.verifyFull];
const MYSQL_SSL_MODE_OPTIONS = sslModeOptions(MYSQL_MODES);

export const mysqlFamilySchema = z.object({
	host: nonEmptyString(),
	port: z.number(),
	user: nonEmptyString(),
	password: nonEmptyString(),
	dbname: nonEmptyString(),
	ssl: sslSchema({ modes: MYSQL_MODES, clientAuth: false }),
});

export type MysqlFamilyOptions = z.infer<typeof mysqlFamilySchema> & {
	connectionString?: string;
};

export interface MysqlFamilyConnectionStringParseResult {
	options: Partial<MysqlFamilyOptions>;
	issues: ConnectionStringIssue[];
}

type MysqlFamilyConnectorId = 'mysql' | 'mariadb';

export function parseMysqlFamilyConnectionString(
	connectionString: string,
	protocols: string[],
): MysqlFamilyConnectionStringParseResult {
	const { options, issues } = parseConnectionString({
		connectionString,
		protocols,
		sslKeys: ['ssl-mode', 'sslmode'],
		sslAllowedModes: MYSQL_MODES,
		// `prefer` is recognized so a pasted MySQL `PREFERRED` surfaces a blocking
		// error rather than being silently dropped.
		sslUnsupportedModes: ['prefer'],
		sslModeAliases: {
			disabled: 'disable',
			required: 'require',
			preferred: 'prefer',
			verify_ca: 'verify-ca',
			verify_identity: 'verify-full',
		},
	});

	return { options: options as Partial<MysqlFamilyOptions>, issues };
}

export function getMysqlConnectorFormSuggestion(
	connectionString: string | undefined,
): ConnectorFormSuggestion | undefined {
	if (!connectionString) return undefined;

	try {
		const url = new URL(connectionString);
		if (url.protocol !== 'mariadb:') return undefined;
	}
	catch {
		return undefined;
	}

	return {
		type: 'switch-connector',
		color: 'warning',
		title: '$t:connectors.mysql_family.mariadb_connection_string_warning',
		description: '$t:connectors.mysql_family.mariadb_connection_string_warning_description',
		actionLabel: '$t:connectors.mysql_family.switch_to_mariadb',
		connector: 'mariadb',
	};
}

interface MysqlFamilyConnectorOptions {
	id: MysqlFamilyConnectorId;
	name: string;
	description: string;
	icon: string;
	iconColor: string;
	defaultPort: number;
	connectionStringPlaceholder: string;
	connectionStringProtocols: string[];
	keywords: string[];
}

function createMysqlFamilyConnectorConfig(
	options: MysqlFamilyConnectorOptions,
): ConnectorConfig<MysqlFamilyOptions, undefined> & { extends?: never } {
	return {
		id: options.id,
		provider: options.id,
		name: options.name,
		description: options.description,
		type: ConnectorType.Relational,
		availableSince: '2026-06-01',

		icon: options.icon,
		iconColor: options.iconColor,

		keywords: [...options.keywords, 'relational', 'sql'],

		options: ({ form, values }) => {
			return form([
				{
					name: 'connectionString',
					label: '$t:connectors.common.connection_string',
					block: BuiltinBlockIds.InputTextarea,
					description: '$t:connectors.common.connection_string_hint',
					fieldProps: {
						layout: 'stacked',
					},
					props: {
						class: 'font-mono font-medium',
						rows: 2,
						placeholder: options.connectionStringPlaceholder,
					},
				},
				{
					type: 'separator',
				},
				{
					name: 'host',
					label: '$t:common.host',
					description: '$t:connectors.common.host_description',
					block: BuiltinBlockIds.Input,
					required: true,
				},
				{
					name: 'port',
					label: '$t:common.port',
					description: '$t:connectors.common.port_description',
					block: BuiltinBlockIds.InputNumber,
					required: true,
					props: {
						formatOptions: {
							useGrouping: false,
						},
						min: 0,
						max: 65_535,
					},
					default: () => options.defaultPort,
				},
				{
					name: 'user',
					label: '$t:common.user',
					description: '$t:connectors.common.user_description',
					block: BuiltinBlockIds.Input,
					required: true,
					props: {
						autocomplete: 'off',
					},
				},
				{
					name: 'password',
					label: '$t:common.password',
					description: '$t:connectors.common.password_description',
					block: BuiltinBlockIds.InputPassword,
					required: true,
					props: {
						autocomplete: 'off',
					},
				},
				{
					name: 'dbname',
					label: '$t:connectors.common.database',
					description: '$t:connectors.common.database_description',
					block: BuiltinBlockIds.Input,
					required: true,
				},
				{
					type: 'separator',
				},
				{
					name: 'ssl.mode',
					label: '$t:connectors.common.ssl_mode',
					description: '$t:connectors.common.ssl_mode_description',
					block: BuiltinBlockIds.InputSelect,
					props: {
						options: MYSQL_SSL_MODE_OPTIONS,
					},
					default: () => 'disable',
				},
				caCertField(values),
			]);
		},

		schema: mysqlFamilySchema,

		onOptionsChange: createConnectionStringOptionsChangeHandler<MysqlFamilyOptions>({
			parse: (connectionString) => parseMysqlFamilyConnectionString(connectionString, options.connectionStringProtocols),
			invalidMessage: '$t:connectors.common.invalid_connection_string',
			fields: ['host', 'port', 'user', 'password', 'dbname', 'ssl'],
			requiredFields: ['host', 'user', 'password', 'dbname'],
		}),

		transformOptions: (opts) => ({
			host: opts.host,
			port: opts.port,
			user: opts.user,
			password: opts.password,
			dbname: opts.dbname,
			ssl: opts.ssl,
		}),

		supportedPrimitiveTypes: [...SUPPORTED_PRIMITIVE_TYPES],
	};
}

export function createMysqlFamilyConnector(options: MysqlFamilyConnectorOptions) {
	return defineConnector<MysqlFamilyOptions>(createMysqlFamilyConnectorConfig(options));
}

export default defineConnector<MysqlFamilyOptions>({
	...createMysqlFamilyConnectorConfig({
		id: 'mysql',
		name: '$t:connectors.mysql.name',
		description: '$t:connectors.mysql.description',
		icon: 'simple-icons:mysql',
		iconColor: '#00758f',
		defaultPort: 3306,
		connectionStringPlaceholder: 'mysql://user:password@host:3306/database',
		connectionStringProtocols: ['mysql:'],
		keywords: ['mysql'],
	}),
	getFormSuggestion: ({ values }) => getMysqlConnectorFormSuggestion(values.connectionString),
});
